import React, { FC, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Flex, Spinner } from '@chakra-ui/react';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  CalendarIcon,
  MusicNotesIcon,
  CaretDownIcon,
  CaretRightIcon,
  TrashIcon,
  PencilSimpleIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

import { useEndpoints } from '../../../hooks/useEndpoints';
import { useOccurrencesList, IOccurrence } from '../../../hooks/useOccurrences';
import { useConfirmModal } from '../../../hooks/useConfirmModal/useConfirmModal';
import Typhography from '../../../shared/Typhography/Typhography';
import {
  WORKOUT_CATEGORY,
  WORKOUT_CATEGORY_COLORS,
  WORKOUT_CATEGORY_LABELS,
  ASSIGNMENT_TYPE,
  WORKOUT_LOG_STATUS,
} from '../../../enums/programming';
import { IWorkoutAssignment, IWorkout, IWorkoutLog } from '../../../models/programming';

import OccurrenceDetailModal from '../OccurrenceDetailModal/OccurrenceDetailModal';
import WorkoutPlanCompileModal from '../WorkoutPlanCompileModal/WorkoutPlanCompileModal';
import WorkoutDetailModal from '../WorkoutDetailModal/WorkoutDetailModal';
import WorkoutBuilderModal from '../WorkoutBuilderModal/WorkoutBuilderModal';

import styles from './WorkoutPlanActivitySection.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkoutPlanActivitySectionProps {
  teamId?: string;
  athleteId?: string;
}

interface OccurrenceModalState {
  open: boolean;
  eventId: string | undefined;
  assignmentId: string | undefined;
}

interface CompileModalState {
  open: boolean;
  assignmentId: string;
  workout: IWorkout | null;
  isPlaylist: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getWorkoutTitle(assignment: IWorkoutAssignment): string {
  if (typeof assignment.workoutId === 'object' && assignment.workoutId !== null) {
    return (assignment.workoutId as IWorkout).title;
  }
  return 'Scheda';
}

function getWorkoutCategory(assignment: IWorkoutAssignment): WORKOUT_CATEGORY | null {
  if (typeof assignment.workoutId === 'object' && assignment.workoutId !== null) {
    const w = assignment.workoutId as IWorkout;
    return w.workoutCategory ?? null;
  }
  return null;
}

function getWorkoutObject(assignment: IWorkoutAssignment): IWorkout | null {
  if (typeof assignment.workoutId === 'object' && assignment.workoutId !== null) {
    return assignment.workoutId as IWorkout;
  }
  return null;
}

function formatDatesCompact(dates: string[], maxShown = 5): string {
  if (!dates || dates.length === 0) return '';

  const sorted = [...dates].sort((a, b) => (a < b ? -1 : 1));
  const shown = sorted.slice(0, maxShown);
  const remainder = sorted.length - maxShown;

  // Group by month-year to produce compact output like "1, 3, 5 Apr 2026"
  const groups: Record<string, number[]> = {};
  shown.forEach((d) => {
    const key = dayjs(d).format('MMM YYYY');
    if (!groups[key]) groups[key] = [];
    groups[key].push(dayjs(d).date());
  });

  const parts = Object.entries(groups).map(([monthYear, days]) => {
    return `${days.join(', ')} ${monthYear}`;
  });

  const base = parts.join(' · ');
  return remainder > 0 ? `${base} +${remainder}` : base;
}

function getOccurrenceProgress(occurrence: IOccurrence): { compiled: number; total: number } {
  const total = occurrence.users?.length ?? occurrence.userIds?.length ?? 0;
  const compiled = occurrence.compiledIds?.length ?? 0;
  return { compiled, total };
}

// ─── Sub-component: OccurrenceRows ────────────────────────────────────────────

interface OccurrenceRowsProps {
  assignmentId: string;
  onOccurrenceClick: (occurrence: IOccurrence) => void;
}

const OccurrenceRows: FC<OccurrenceRowsProps> = ({ assignmentId, onOccurrenceClick }) => {
  const { data: occurrences, isLoading } = useOccurrencesList(assignmentId, true);

  if (isLoading) {
    return (
      <div className={styles.occurrenceList}>
        <Flex justify="center" py={2}>
          <Spinner size="sm" />
        </Flex>
      </div>
    );
  }

  if (!occurrences || occurrences.length === 0) {
    return (
      <div className={styles.occurrenceList}>
        <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0' }}>Nessuna occorrenza</p>
      </div>
    );
  }

  return (
    <div className={styles.occurrenceList}>
      {occurrences.map((occ) => {
        const { compiled, total } = getOccurrenceProgress(occ);
        const pct = total > 0 ? Math.round((compiled / total) * 100) : 0;
        const dateLabel = dayjs(occ.startDate).format('ddd D MMM YYYY');

        return (
          <div
            key={occ._id}
            className={styles.occurrenceRow}
            onClick={() => onOccurrenceClick(occ)}
          >
            <span className={styles.occurrenceDate}>{dateLabel}</span>
            <div className={styles.progressBarSmall}>
              <div className={styles.fill} style={{ width: `${pct}%` }} />
            </div>
            <span className={styles.occurrenceCount}>
              {compiled}/{total}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Sub-component: ScheduledCard ─────────────────────────────────────────────

interface ScheduledCardProps {
  assignment: IWorkoutAssignment;
  onDelete: (id: string) => void;
  onEdit: (workout: IWorkout) => void;
  onOccurrenceClick: (occurrence: IOccurrence, assignmentId: string) => void;
}

const ScheduledCard: FC<ScheduledCardProps> = ({ assignment, onDelete, onEdit, onOccurrenceClick }) => {
  const [expanded, setExpanded] = useState(false);
  const category = getWorkoutCategory(assignment);
  const title = getWorkoutTitle(assignment);
  const datesLabel = formatDatesCompact(assignment.scheduledDates ?? []);

  return (
    <div className={styles.assignmentCard}>
      <div className={styles.cardHeader}>
        <p className={styles.cardTitle}>{title}</p>
        <div className={styles.cardActions}>
          <button
            className={styles.occurrenceToggleBtn}
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? 'Nascondi occorrenze' : 'Mostra occorrenze'}
          >
            {expanded ? <CaretDownIcon size={13} weight="bold" /> : <CaretRightIcon size={13} weight="bold" />}
            Occorrenze
          </button>
          {getWorkoutObject(assignment) && (
            <button
              className={styles.actionBtn}
              onClick={() => onEdit(getWorkoutObject(assignment)!)}
              title="Modifica scheda"
            >
              <PencilSimpleIcon size={13} weight="bold" />
              Modifica
            </button>
          )}
          <button
            className={styles.actionBtnDanger}
            onClick={() => onDelete(assignment._id)}
            title="Elimina assegnazione"
          >
            <TrashIcon size={13} weight="bold" />
            Elimina
          </button>
        </div>
      </div>

      <div className={styles.cardMeta}>
        {category && (
          <span
            className={styles.categoryBadge}
            style={{ backgroundColor: WORKOUT_CATEGORY_COLORS[category] }}
          >
            {WORKOUT_CATEGORY_LABELS[category]}
          </span>
        )}
        {datesLabel
          ? <p className={styles.datesCompact}>{datesLabel}</p>
          : <p className={styles.datesCompact} style={{ color: 'var(--c-text-3)', fontStyle: 'italic' }}>Nessuna data programmata</p>
        }
      </div>

      {expanded && (
        <OccurrenceRows
          assignmentId={assignment._id}
          onOccurrenceClick={(occ) => onOccurrenceClick(occ, assignment._id)}
        />
      )}
    </div>
  );
};

// ─── Sub-component: PlaylistCard ──────────────────────────────────────────────

interface PlaylistCardProps {
  assignment: IWorkoutAssignment;
  onDelete: (id: string) => void;
  onDetail: (workoutId: string) => void;
  onCompile: (assignment: IWorkoutAssignment) => void;
  workoutLog?: IWorkoutLog;
}

const PlaylistCard: FC<PlaylistCardProps> = ({ assignment, onDelete, onDetail, onCompile, workoutLog }) => {
  const isCompleted =
    workoutLog?.status === WORKOUT_LOG_STATUS.COMPLETED ||
    workoutLog?.status === WORKOUT_LOG_STATUS.COMPLETED_LATE ||
    !!workoutLog?.completedAt;
  const completedDate = workoutLog?.completedAt
    ? dayjs(workoutLog.completedAt).locale('it').format('D MMM YYYY')
    : null;
  const category = getWorkoutCategory(assignment);
  const title = getWorkoutTitle(assignment);
  const workoutId =
    typeof assignment.workoutId === 'object' && assignment.workoutId !== null
      ? (assignment.workoutId as IWorkout)._id
      : (assignment.workoutId as string);

  return (
    <div className={styles.assignmentCard}>
      <div className={styles.cardHeader}>
        <p className={styles.cardTitle}>{title}</p>
        <div className={styles.cardActions}>
          <button
            className={styles.actionBtn}
            onClick={() => onDetail(workoutId)}
            title="Dettaglio scheda"
          >
            Dettaglio
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => onCompile(assignment)}
            title="Compila scheda"
          >
            Compila
          </button>
          <button
            className={styles.actionBtnDanger}
            onClick={() => onDelete(assignment._id)}
            title="Elimina assegnazione"
          >
            <TrashIcon size={13} weight="bold" />
            Elimina
          </button>
        </div>
      </div>

      <div className={styles.cardMeta}>
        <span className={styles.playlistBadge}>Playlist</span>
        {category && (
          <span
            className={styles.categoryBadge}
            style={{ backgroundColor: WORKOUT_CATEGORY_COLORS[category] }}
          >
            {WORKOUT_CATEGORY_LABELS[category]}
          </span>
        )}
        {isCompleted && completedDate ? (
          <span className={styles.completedBadge}>
            <CheckCircleIcon size={13} weight="fill" />
            Svolto il {completedDate}
          </span>
        ) : (
          <span className={styles.pendingBadge}>Da svolgere</span>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const WorkoutPlanActivitySection: FC<WorkoutPlanActivitySectionProps> = ({ teamId, athleteId }) => {
  const queryClient = useQueryClient();
  const { getProgrammingAssignments, deleteProgrammingAssignment, getAthleteLogs } = useEndpoints();
  const { handleOpen: openConfirmModal } = useConfirmModal();

  // ── Modal state ──
  const [occurrenceModal, setOccurrenceModal] = useState<OccurrenceModalState>({
    open: false,
    eventId: undefined,
    assignmentId: undefined,
  });
  const [compileModal, setCompileModal] = useState<CompileModalState>({
    open: false,
    assignmentId: '',
    workout: null,
    isPlaylist: false,
  });
  const [detailWorkoutId, setDetailWorkoutId] = useState<string | null>(null);
  const [editingWorkout, setEditingWorkout] = useState<IWorkout | null>(null);

  // ── Queries ──
  const baseParams = teamId ? { teamId } : { athleteId };

  const scheduledQuery = useQuery({
    queryKey: ['programming-assignments', baseParams, 'scheduled'],
    queryFn: async () => {
      const { data } = await getProgrammingAssignments({
        ...baseParams,
        assignmentType: ASSIGNMENT_TYPE.SCHEDULED,
        limit: 50,
      } as any);
      return data;
    },
    enabled: !!(teamId || athleteId),
    staleTime: 60_000,
  });

  const playlistQuery = useQuery({
    queryKey: ['programming-assignments', baseParams, 'playlist'],
    queryFn: async () => {
      const { data } = await getProgrammingAssignments({
        ...baseParams,
        assignmentType: ASSIGNMENT_TYPE.PLAYLIST,
        limit: 50,
      } as any);
      return data;
    },
    enabled: !!(teamId || athleteId),
    staleTime: 60_000,
  });

  // ── Delete mutation ──
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgrammingAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programming-assignments'] });
      toast.success('Assegnazione eliminata');
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione');
    },
  });

  const handleDelete = (id: string) => {
    openConfirmModal({
      title: 'Elimina assegnazione',
      description: 'Sei sicuro di voler eliminare questa assegnazione? L\'operazione non può essere annullata.',
      onConfirm: () => deleteMutation.mutate(id),
    });
  };

  const handleOccurrenceClick = (occurrence: IOccurrence, assignmentId: string) => {
    setOccurrenceModal({
      open: true,
      eventId: occurrence._id,
      assignmentId,
    });
  };

  const handleCompile = (assignment: IWorkoutAssignment) => {
    const workout = getWorkoutObject(assignment);
    setCompileModal({
      open: true,
      assignmentId: assignment._id,
      workout,
      isPlaylist: true,
    });
  };

  const athleteLogsQuery = useQuery({
    queryKey: ['athlete-logs-playlist', athleteId],
    queryFn: async () => {
      const { data } = await getAthleteLogs(athleteId!, { limit: 200 });
      return data;
    },
    enabled: !!athleteId,
    staleTime: 60_000,
  });

  const scheduledAssignments = (scheduledQuery.data?.docs ?? []).filter(
    (a) => a.assignmentType === ASSIGNMENT_TYPE.SCHEDULED,
  );
  const playlistAssignments = (playlistQuery.data?.docs ?? []).filter(
    (a) => a.assignmentType === ASSIGNMENT_TYPE.PLAYLIST,
  );

  // Mappa workoutAssignmentId → log più recente completato (o qualsiasi log)
  const logsByAssignmentId = (athleteLogsQuery.data?.docs ?? []).reduce<Record<string, IWorkoutLog>>(
    (acc, log) => {
      const existing = acc[log.workoutAssignmentId];
      if (
        !existing ||
        log.status === WORKOUT_LOG_STATUS.COMPLETED ||
        log.status === WORKOUT_LOG_STATUS.COMPLETED_LATE
      ) {
        acc[log.workoutAssignmentId] = log;
      }
      return acc;
    },
    {}
  );

  return (
    <div>
      {/* ── Sezione: Schede Programmate ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <CalendarIcon size={18} weight="bold" color="#4a7cff" />
          <Typhography size="bold-16">Schede Programmate</Typhography>
        </div>

        {scheduledQuery.isLoading ? (
          <div className={styles.loadingState}>
            <LeopartLoader size="sm" />
          </div>
        ) : scheduledAssignments.length === 0 ? (
          <div className={styles.emptyState}>Nessuna scheda programmata</div>
        ) : (
          scheduledAssignments.map((assignment) => (
            <ScheduledCard
              key={assignment._id}
              assignment={assignment}
              onDelete={handleDelete}
              onEdit={setEditingWorkout}
              onOccurrenceClick={handleOccurrenceClick}
            />
          ))
        )}
      </div>

      {/* ── Sezione: Playlist ── */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <MusicNotesIcon size={18} weight="bold" color="#34d399" />
          <Typhography size="bold-16">Playlist</Typhography>
        </div>

        {playlistQuery.isLoading ? (
          <div className={styles.loadingState}>
            <LeopartLoader size="sm" />
          </div>
        ) : playlistAssignments.length === 0 ? (
          <div className={styles.emptyState}>Nessuna playlist</div>
        ) : (
          playlistAssignments.map((assignment) => (
            <PlaylistCard
              key={assignment._id}
              assignment={assignment}
              onDelete={handleDelete}
              onDetail={(wId) => setDetailWorkoutId(wId)}
              onCompile={handleCompile}
              workoutLog={logsByAssignmentId[assignment._id]}
            />
          ))
        )}
      </div>

      {/* ── Modali ── */}
      {occurrenceModal.open && (
        <OccurrenceDetailModal
          isOpen={occurrenceModal.open}
          onClose={() => setOccurrenceModal({ open: false, eventId: undefined, assignmentId: undefined })}
          eventId={occurrenceModal.eventId}
          workoutAssignmentId={occurrenceModal.assignmentId}
          onEdit={() => {}}
          onDelete={() => {
            setOccurrenceModal({ open: false, eventId: undefined, assignmentId: undefined });
          }}
          onCompile={() => {}}
        />
      )}

      {compileModal.open && (
        <WorkoutPlanCompileModal
          isOpen={compileModal.open}
          onClose={() => setCompileModal({ open: false, assignmentId: '', workout: null, isPlaylist: false })}
          assignmentId={compileModal.assignmentId}
          workout={compileModal.workout}
          athletes={[]}
          isPlaylist={compileModal.isPlaylist}
        />
      )}

      {detailWorkoutId && (
        <WorkoutDetailModal
          workoutId={detailWorkoutId}
          onClose={() => setDetailWorkoutId(null)}
        />
      )}

      {editingWorkout && (
        <WorkoutBuilderModal
          isOpen={!!editingWorkout}
          onClose={() => setEditingWorkout(null)}
          workout={editingWorkout}
          previewAthleteId={athleteId}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['programming-assignments'] });
            setEditingWorkout(null);
          }}
        />
      )}
    </div>
  );
};

export default WorkoutPlanActivitySection;
